<script setup lang="ts">
	import type { MarkdownHeading } from '~/composables/useMarkdownToc'

	const props = defineProps<{
		headings: MarkdownHeading[]
	}>()

	const emit = defineEmits<{
		'scroll-to': [id: string]
	}>()

	const searchInput = useTemplateRef<HTMLInputElement>('searchInput')
	const searchQuery = ref('')

	const searchResults = computed(() => {
		if (!searchQuery.value) return []
		const query = searchQuery.value.toLowerCase()
		return props.headings.filter(h => h.text.toLowerCase().includes(query))
	})

	const handleKeyDown = (e: KeyboardEvent) => {
		if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
			e.preventDefault()
			searchInput.value?.focus()
		}
	}

	onMounted(() => {
		window.addEventListener('keydown', handleKeyDown)
	})

	onUnmounted(() => {
		window.removeEventListener('keydown', handleKeyDown)
	})

	const selectResult = (id: string) => {
		emit('scroll-to', id)
		searchQuery.value = ''
	}
</script>

<template>
	<div class="relative hidden w-full max-w-md md:block">
		<div class="relative flex items-center">
			<svg class="absolute left-3 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
				></path>
			</svg>
			<input
				ref="searchInput"
				v-model="searchQuery"
				type="text"
				placeholder="Search documentation..."
				class="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-4 text-sm shadow-sm transition-all placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
			/>
			<div class="pointer-events-none absolute right-3 flex items-center">
				<kbd
					class="hidden rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 sm:inline-block"
					>CMD K</kbd
				>
			</div>
		</div>

		<!-- Search Results Dropdown -->
		<div
			v-if="searchQuery && searchResults.length > 0"
			class="absolute left-0 top-full z-50 mt-2 w-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl shadow-gray-200/50"
		>
			<ul class="max-h-[60vh] space-y-0.5 overflow-y-auto p-2">
				<li v-for="result in searchResults" :key="result.id">
					<button
						class="group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-primary-50"
						:class="result.depth === 3 ? 'pl-8 text-gray-600' : 'font-medium text-gray-900'"
						@click="selectResult(result.id)"
					>
						<svg
							v-if="result.depth === 2"
							class="h-4 w-4 text-gray-400 group-hover:text-primary-500"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 5l7 7-7 7"
							></path>
						</svg>
						<svg
							v-else
							class="h-3 w-3 text-gray-300 group-hover:text-primary-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 5l7 7-7 7"
							></path>
						</svg>
						{{ result.text }}
					</button>
				</li>
			</ul>
		</div>
		<div
			v-else-if="searchQuery"
			class="absolute left-0 top-full z-50 mt-2 w-full rounded-xl border border-gray-100 bg-white p-6 text-center shadow-xl shadow-gray-200/50"
		>
			<p class="text-sm text-gray-500"
				>No results found for "<span class="font-medium text-gray-900">{{ searchQuery }}</span
				>"</p
			>
		</div>
	</div>
</template>
